import * as React from 'react';

import { useFormContext, Controller } from 'react-hook-form';
import { Box, Stack, Paper, IconButton, Button, Typography, TextField, MenuItem } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Unstable_Grid2';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import _ from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

const resolveNewRow = (newRow) => {
  if (typeof newRow === 'function') {
    return newRow();
  }
  if (newRow && typeof newRow === 'object') {
    return newRow;
  }
  return {};
};

const FormObjectArrayListBody = ({
  field,
  name,
  label,
  required,
  fields,
  newRow,
  buttonText,
  labelProps,
  rowProps,
  stackProps,
  showRowIndex,
  emptyText,
  errorMessage,
}) => {
  const { value, onChange } = field;
	const resolvedValue = React.useMemo(() => { return Array.isArray(value) ? value : []; }, [value]);
  const [rows, setRows] = React.useState(resolvedValue);

  React.useEffect(() => {
    if (!_.isEqual(resolvedValue, rows)) {
      setRows(resolvedValue);
    }
  }, [resolvedValue, rows]);

  const updateRows = (nextRows) => {
    setRows(nextRows);
    onChange(nextRows);
  };

  const handleAdd = () => {
    const nextRows = [...rows, resolveNewRow(newRow)];
    updateRows(nextRows);
  };

  const handleRemove = (index) => {
    const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
    updateRows(nextRows);
  };

  const handleFieldChange = (index, key) => (event) => {
    const nextValue = event?.target?.value ?? event;
    const nextRows = [...rows];
    const nextRow = { ...(nextRows[index] || {}) };
    nextRow[key] = nextValue;
    nextRows[index] = nextRow;
    updateRows(nextRows);
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {label ? (
        <InputLabel required={required} sx={{ mb: 1 }} {...labelProps}>
          {label}
        </InputLabel>
      ) : null}
      <Stack spacing={2} {...stackProps}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : (
          rows.map((row, index) => (
            <Paper key={row.id || `${name}-${index}`} variant="outlined" sx={{ p: 2 }} {...rowProps}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ flexGrow: 1 }}>
                  {showRowIndex ? (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Row {index + 1}
                    </Typography>
                  ) : null}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      {fields.map((fieldConfig) => {
                        const InputComponent = fieldConfig.component || TextField;
                        const fieldLabel = fieldConfig.label || fieldConfig.key;
                        const gridProps = fieldConfig.gridProps || { xs: 12 };
                        const componentProps =
                          typeof fieldConfig.componentProps === 'function'
                            ? fieldConfig.componentProps(row, index, rows)
                            : fieldConfig.componentProps || {};
                        const options =
                          typeof fieldConfig.options === 'function'
                            ? fieldConfig.options(row, index, rows)
                            : fieldConfig.options || [];
                        const isSelect = componentProps.select || fieldConfig.type === 'select' || options.length > 0;
                        const isTime = fieldConfig.type === 'time';
                        const inputValue = row?.[fieldConfig.key] ?? (isTime ? null : '');

                        return (
                          <Grid key={`${index}-${fieldConfig.key}`} {...gridProps}>
                            {isTime ? (
                              <TimePicker
                                label={fieldLabel}
                                value={inputValue}
                                onChange={(newValue) => handleFieldChange(index, fieldConfig.key)(newValue)}
                                slotProps={{ textField: { fullWidth: true, ...componentProps } }}
                              />
                            ) : (
                              <InputComponent
                                label={fieldLabel}
                                value={inputValue}
                                onChange={handleFieldChange(index, fieldConfig.key)}
                                fullWidth
                                {...(isSelect ? { select: true } : {})}
                                {...componentProps}
                              >
                                {isSelect
                                  ? options.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))
                                  : null}
                              </InputComponent>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </LocalizationProvider>
                </Box>
                <IconButton aria-label="Remove row" onClick={() => handleRemove(index)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Paper>
          ))
        )}
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
          {buttonText}
        </Button>
        {errorMessage ? <FormHelperText error>{errorMessage}</FormHelperText> : null}
      </Stack>
    </Box>
  );
};

const FormObjectArrayList = ({
  name,
  label,
  required,
  rules = {},
  fields = [],
  newRow = () => ({}),
  buttonText = 'Add Row',
  labelProps = {},
  rowProps = {},
  stackProps = {},
  showRowIndex = true,
  emptyText = 'No items yet.',
}) => {
  const { control, formState } = useFormContext();
  const errors = formState.errors;
  const errorMessage = errors?.[name]?.message ? String(errors[name]?.message) : '';

  const requiredDefaultRule = required
    ? { required: { value: true, message: 'This field is required' } }
    : {};
  const rowValidationRule = {
    validate: (value) => {
      const rows = Array.isArray(value) ? value : [];
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i] || {};
        for (const fieldConfig of fields) {
          const rawComponentProps = fieldConfig.componentProps || {};
          const componentProps =
            typeof rawComponentProps === 'function' ? rawComponentProps(row, i, rows) : rawComponentProps;
          const isRequired = Boolean(
            fieldConfig.required || componentProps?.required || fieldConfig.rules?.required
          );
          if (!isRequired) continue;
          const fieldValue = row?.[fieldConfig.key];
          const isEmpty =
            fieldValue === null ||
            fieldValue === undefined ||
            (typeof fieldValue === 'string' && fieldValue.trim() === '');
          if (isEmpty) {
            const fieldLabel = fieldConfig.label || fieldConfig.key;
            return `${fieldLabel} is required`;
          }
        }
      }
      return true;
    },
  };
  rules = { ...requiredDefaultRule, ...rowValidationRule, ...rules };

  return (
    <Controller
      render={({ field }) => (
        <FormObjectArrayListBody
          field={field}
          name={name}
          label={label}
          required={required}
          fields={fields}
          newRow={newRow}
          buttonText={buttonText}
          labelProps={labelProps}
          rowProps={rowProps}
          stackProps={stackProps}
          showRowIndex={showRowIndex}
          emptyText={emptyText}
          errorMessage={errorMessage}
        />
      )}
      name={name}
      control={control}
      rules={rules}
    />
  );
};

export default FormObjectArrayList;
